<script>
import { mapGetters } from 'vuex';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import BackButton from '../BackButton.vue';
import inboxMixin from 'shared/mixins/inboxMixin';
import InboxName from '../InboxName.vue';
import Thumbnail from '../Thumbnail.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import Linear from './linear/index.vue';
import MoreActions from './MoreActions.vue';

export default {
  components: {
    BackButton,
    InboxName,
    Thumbnail,
    SLACardLabel,
    Linear,
    MoreActions
  },
  mixins: [inboxMixin],
  props: {
    chat: {
      type: Object,
      default: () => {},
    },
    isContactPanelOpen: {
      type: Boolean,
      default: false,
    },
    showBackButton: {
      type: Boolean,
      default: false,
    },
    showSlaCard: {
      type: Boolean,
      default: false,
    },
    showLinearCard: {
      type: Boolean,
      default: false,
    },
    showMoreActions: {
      type: Boolean,
      default: false,
    },
    isOnExpandedLayout: {
      type: Boolean,
      default: false,
    },
    toggleConversationLayout: {
      type: Function,
      default: () => {},
    },
  },
  emits: ['contactPanelToggle'],
  setup(props, { emit }) {
    const keyboardEvents = {
      'Alt+KeyO': {
        action: () => emit('contactPanelToggle'),
      },
    };
    useKeyboardEvents(keyboardEvents);
  },
  methods: {
    onHeaderMainButtonClick() {
      if (this.isContactPanelOpen) {
        const path = `/app/accounts/${this.accountId}/contacts/${this.currentContact.id}`;
        this.$router.push(path);
      } else {
        this.$emit('contactPanelToggle');
      }
    },
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      appIntegrations: 'integrations/getAppIntegrations',
    }),
    chatMetadata() {
      return this.chat.meta;
    },
    backButtonUrl() {
      const {
        params: { accountId, inbox_id: inboxId, label, teamId },
        name,
      } = this.$route;
      return conversationListPageURL({
        accountId,
        inboxId,
        label,
        teamId,
        conversationType: name === 'conversation_mentions' ? 'mention' : '',
      });
    },
    isHMACVerified() {
      if (!this.isAWebWidgetInbox) {
        return true;
      }
      return this.chatMetadata.hmac_verified;
    },
    currentContact() {
      return this.$store.getters['contacts/getContact'](
        this.chat.meta.sender.id
      );
    },
    isSnoozed() {
      return this.currentChat.status === wootConstants.STATUS_TYPE.SNOOZED;
    },
    snoozedDisplayText() {
      const { snoozed_until: snoozedUntil } = this.currentChat;
      if (snoozedUntil) {
        return `${this.$t(
          'CONVERSATION.HEADER.SNOOZED_UNTIL'
        )} ${snoozedReopenTime(snoozedUntil)}`;
      }
      return this.$t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
    },
    contactPanelToggleText() {
      return `${
        this.isContactPanelOpen
          ? this.$t('CONVERSATION.HEADER.CLOSE')
          : this.$t('CONVERSATION.HEADER.OPEN')
      }`;
    },
    inbox() {
      const { inbox_id: inboxId } = this.chat;
      return this.$store.getters['inboxes/getInbox'](inboxId);
    },
    hasMultipleInboxes() {
      return this.$store.getters['inboxes/getInboxes'].length > 1;
    },
    hasSlaPolicyId() {
      return this.chat?.sla_policy_id;
    },
    isLinearIntegrationEnabled() {
      return this.appIntegrations.find(
        integration => integration.id === 'linear' && !!integration.hooks.length
      );
    },
    isLinearFeatureEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.LINEAR
      );
    },
  },
};
</script> 

<template>
<div>
  <div
    :class="[
      'flex flex-col items-center justify-between w-full px-3 pl-16 xl:pl-0 py-4 xl:py-3 pb-0 xl:pb-3 xl:flex-row rounded-t-lg',
      isContactPanelOpen 
        ? 'border border-slate-50 dark:border-n-gray-8' 
        : 'border-b border-slate-50 dark:border-n-gray-8'
    ]"
  >
    <div
      class="flex flex-col items-center justify-center flex-1 w-full min-w-0 xl:flex-row"
    >
      <div class="flex items-center justify-between w-full min-w-0 overflow-hidden">
        <div class="flex gap-3 items-center min-w-0 flex-1">
        <!-- Layout Toggle Button -->
        <button
          class="ml-3 flex items-center justify-center  w-9 h-9 rounded-lg bg-n-gray-1 dark:bg-n-gray-7 hover:bg-n-gray-3 dark:hover:bg-slate-600 transition-colors duration-200 text-slate-600 dark:text-slate-400 hidden xl:flex"
          :title="!isOnExpandedLayout ? 'Hide conversation list' : 'Show conversation list'"
          @click="toggleConversationLayout"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              v-if="!isOnExpandedLayout"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2.5"
              d="M15 19l-7-7 7-7"
            />
            <path
              v-else
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2.5"
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
        </button>
        
        <BackButton
          v-if="true"
          :back-url="backButtonUrl"
          class="ltr:ml-0 rtl:mr-0 rtl:ml-4 xl:hidden"
        />
        <Thumbnail
          class="hidden md:flex"
          :size="'40px'"
          :src="currentContact.thumbnail"
          :badge="inboxBadge"
          :username="currentContact.name"
          :status="currentContact.availability_status"
        />
        <div
          class="flex flex-col items-start min-w-0 overflow-hidden rtl:ml-0 rtl:mr-2 flex-1"
          >
          <div
            class="flex items-center gap-2 overflow-hidden text-xs conversation--header--actions text-ellipsis whitespace-nowrap"
          >
            <InboxName :showInboxIcon="false" v-if="hasMultipleInboxes" :inbox="inbox" />
            <span
              v-if="isSnoozed"
              class="font-medium text-yellow-600 dark:text-yellow-500"
            >
              {{ snoozedDisplayText }}
            </span>
          </div>
          <div
            class="flex flex-row items-center gap-1 p-0 m-0 flex-1 min-w-0"
          >
            <div class="min-w-0 max-w-full flex-1 basis-0 overflow-hidden">
              <span
                class="w-full max-w-[120px] md:max-w-none truncate block text-text-base font-medium leading-tight text-slate-900 dark:text-n-gray-3"
                :title="currentContact.name"
              >
                {{ currentContact.name }}
              </span>
            </div>
            <fluent-icon
              v-if="!isHMACVerified"
              v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
              size="14"
              class="text-yellow-600 dark:text-yellow-500 my-0 mx-0 min-w-[14px]"
              icon="warning"
            />
          </div>
          <!-- Platform/Channel info -->
          <div class="text-xs text-slate-500 dark:text-slate-400 font-medium hidden xl:block">
            <span v-if="inbox.channel_type">
              {{ $t(`INBOX.CHANNEL_TYPE.${inbox.channel_type.toUpperCase()}`) }}
            </span>
            <span v-if="inbox.name && inbox.channel_type" class="mx-1">•</span>
            <span v-if="inbox.name">{{ inbox.name }}</span>
          </div>      
        </div>
        </div>
        <!-- Contact Panel Toggle Button (match Woot button style) -->
        <MoreActions />
        <woot-button
          v-tooltip="$t('CONTACT_PANEL.TOGGLE_CONTACT_PANEL')"
          variant="clear"
          color-scheme="secondary"
          class="shrink-0 ml-2"
          :icon="isContactPanelOpen ? 'i-lucide-user-x' : 'i-lucide-user-search'"
          @click="$emit('contactPanelToggle')"
        />
      </div>
      <div
        class="flex flex-row items-center justify-end flex-grow gap-2 mt-3 header-actions-wrap xl:mt-0"
        :class="{ 'justify-end': isContactPanelOpen }"
      >
        <SLACardLabel v-if="hasSlaPolicyId && showSlaCard" :chat="chat" show-extended-info />
        <Linear
          v-if="isLinearIntegrationEnabled && isLinearFeatureEnabled && showLinearCard"
          :conversation-id="currentChat.id"
        />
        
      </div>
    </div>
  </div>
</div>
</template>

<style lang="scss" scoped>
.conversation--header--actions {
  ::v-deep .inbox--name {
    @apply m-0;
  }
}
</style>
